import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, EnvelopeSimple, Phone, MapPin, Buildings } from '@phosphor-icons/react';

const COMPANY = {
  name: 'AXIALUP OPC PVT LTD',
  gstin: '10AAYCA0050H1Z1',
  state: 'Bihar (10)',
  email: 'support@axialup.in',
  phone: '9128843229',
  address: '1st Floor, Ward No. 13, Kapraul Siroman, Sitamarhi, Bihar - 843302, India',
};

function PolicyLayout({ title, children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0C1D3F] text-white">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center gap-4">
          <Link to="/login" className="text-white/70 hover:text-white transition-colors">
            <ArrowLeft size={22} />
          </Link>
          <div>
            <p className="text-[10px] uppercase tracking-[2px] text-white/50 mb-0.5">Axial Up</p>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight">{title}</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-6 sm:px-8 py-8 prose prose-sm prose-gray max-w-none
            [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-[#0C1D3F] [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-gray-100
            [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2
            [&_p]:text-sm [&_p]:text-gray-600 [&_p]:leading-relaxed [&_p]:mb-3
            [&_ul]:text-sm [&_ul]:text-gray-600 [&_ul]:pl-5 [&_ul]:mb-3 [&_ul>li]:mb-1
            [&_strong]:text-gray-800">
            {children}
          </div>
        </div>

        {/* Footer Nav */}
        <div className="mt-6 flex flex-wrap gap-3 justify-center text-xs">
          {[
            { to: '/terms', label: 'Terms & Conditions' },
            { to: '/privacy', label: 'Privacy Policy' },
            { to: '/refund-policy', label: 'Refund Policy' },
            { to: '/grievance-policy', label: 'Grievance Policy' },
            { to: '/support-help', label: 'Support & Help' },
            { to: '/data-deletion', label: 'Data Deletion' },
            { to: '/whatsapp-compliance', label: 'WhatsApp Policy' },
            { to: '/about-business', label: 'About Business' },
          ].map(l => (
            <Link key={l.to} to={l.to} className="text-[#002FA7] hover:underline font-medium">{l.label}</Link>
          ))}
        </div>

        <p className="text-center text-[10px] text-gray-400 mt-4 mb-8">{COMPANY.name} | GSTIN: {COMPANY.gstin}</p>
      </main>
    </div>
  );
}


/* Full legal HTML docs are authoritative source (Meta-compliance templates uploaded Feb 2026).
 * We iframe them here so React routes like /terms render identical text to /terms-and-conditions.html
 * which Meta reviewers hit directly. Emails have been scrubbed to only support@axialup.in + raviraj@axialup.in. */
const POLICY_VERSION = 'v2.1';
const POLICY_VERIFIED_ON = 'Feb 04, 2026';

function FullPolicyFrame({ title, src }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-[#0C1D3F] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/login" className="text-white/70 hover:text-white transition-colors">
            <ArrowLeft size={22} />
          </Link>
          <div>
            <p className="text-[10px] uppercase tracking-[2px] text-white/50 mb-0.5">AXIALUP OPC PVT LTD</p>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight">{title}</h1>
          </div>
          <a href={src} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded" data-testid="policy-open-raw">Open full document ↗</a>
          <Link to="/support" className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-3 py-1.5 rounded shadow-sm" data-testid="policy-raise-grievance">
            🚨 Raise Grievance / Ticket
          </Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto w-full flex-1">
        <iframe
          src={src}
          title={title}
          className="w-full border-0"
          style={{ height: 'calc(100vh - 160px)', minHeight: '660px' }}
          data-testid="policy-iframe"
        />
      </main>
      <footer className="bg-white border-t border-gray-200 py-2.5 px-4" data-testid="policy-version-footer">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-500">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-2 py-0.5 font-semibold">
              ✓ Policy {POLICY_VERSION}
            </span>
            <span>Last verified: <b>{POLICY_VERIFIED_ON}</b></span>
          </div>
          <div className="text-[10px] text-gray-400">
            AXIALUP (OPC) PRIVATE LIMITED · CIN U72200BR2022OPC060903 · This document is maintained and reviewed regularly.
          </div>
        </div>
      </footer>
    </div>
  );
}


export function TermsPage() {
  return <FullPolicyFrame title="Terms & Conditions" src="/terms-and-conditions.html" />;
}


export function PrivacyPage() {
  return <FullPolicyFrame title="Privacy Policy" src="/privacy-policy.html" />;
}


export function RefundPolicyPage() {
  return <FullPolicyFrame title="Refund & Cancellation Policy" src="/refund-policy.html" />;
}


export function GrievancePolicyPage() {
  return <FullPolicyFrame title="Grievance Redressal Policy" src="/grievance-policy.html" />;
}


export function SupportHelpPage() {
  return (
    <PolicyLayout title="Support & Help">
      <p className="!text-xs !text-gray-400 !mb-6">Last updated: April 2026</p>

      <h2>How to Get Help</h2>
      <p>If any user has queries, issues, complaints, or needs assistance, they can raise a support ticket or contact us directly.</p>

      <h3>Users can:</h3>
      <ul>
        <li>Raise a support ticket from dashboard</li>
        <li>Submit enquiry related to booking, payment, refund, or mismatch</li>
        <li>Contact support team via email or mobile</li>
      </ul>

      <h2>Contact Details</h2>
      <div className="not-prose space-y-3 my-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <EnvelopeSimple size={20} className="text-[#002FA7] shrink-0" />
          <div>
            <p className="text-xs text-gray-500">Email</p>
            <a href={`mailto:${COMPANY.email}`} className="text-sm font-medium text-[#002FA7] hover:underline">{COMPANY.email}</a>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Phone size={20} className="text-[#002FA7] shrink-0" />
          <div>
            <p className="text-xs text-gray-500">Mobile</p>
            <a href={`tel:${COMPANY.phone}`} className="text-sm font-medium text-gray-900">{COMPANY.phone}</a>
          </div>
        </div>
      </div>

      <h2>Office Address</h2>
      <div className="not-prose my-4">
        <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <MapPin size={20} className="text-[#002FA7] shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700 leading-relaxed">{COMPANY.address}</p>
        </div>
      </div>

      <h2>GST Details</h2>
      <div className="not-prose my-4">
        <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Buildings size={20} className="text-[#002FA7] shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-gray-900">{COMPANY.name}</p>
            <p className="text-gray-600">GSTIN: {COMPANY.gstin}</p>
            <p className="text-gray-600">State: {COMPANY.state}</p>
          </div>
        </div>
      </div>

      <p>Support team will review and respond after proper verification.</p>
    </PolicyLayout>
  );
}


// ─────────────────────────────────────────────────────────────
//  Feb 2026 — Meta WhatsApp Business Verification compliance
//  These 3 pages are REQUIRED by Meta/Facebook before they'll
//  approve a WhatsApp Business API phone number.
// ─────────────────────────────────────────────────────────────

export function DataDeletionPage() {
  return (
    <PolicyLayout title="Data Deletion Request">
      <p className="!text-xs !text-gray-400 !mb-6">Last updated: February 2026</p>

      <h2>Your Right to Deletion</h2>
      <p>As per our Privacy Policy and applicable data protection regulations (including GDPR Article 17 and India's DPDP Act 2023), you have the right to request deletion of your personal data stored on <strong>{COMPANY.name}</strong> platform.</p>

      <h2>What Data Can Be Deleted</h2>
      <ul>
        <li>Profile information (name, email, phone number)</li>
        <li>KYC documents and identity proofs</li>
        <li>Transaction and booking history</li>
        <li>Support conversations and tickets</li>
        <li>WhatsApp conversation history (if any)</li>
        <li>Any other personally identifiable information (PII)</li>
      </ul>

      <h2>How to Request Deletion</h2>
      <p>Send a deletion request via any of the following channels:</p>
      <div className="not-prose space-y-3 my-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <EnvelopeSimple size={20} className="text-[#002FA7] shrink-0" />
          <div>
            <p className="text-xs text-gray-500">Email (preferred)</p>
            <a href={`mailto:${COMPANY.email}?subject=Data%20Deletion%20Request`} className="text-sm font-medium text-[#002FA7] hover:underline">{COMPANY.email}</a>
            <p className="text-[11px] text-gray-500 mt-0.5">Subject: "Data Deletion Request"</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Phone size={20} className="text-[#002FA7] shrink-0" />
          <div>
            <p className="text-xs text-gray-500">Phone</p>
            <a href={`tel:${COMPANY.phone}`} className="text-sm font-medium text-gray-900">{COMPANY.phone}</a>
          </div>
        </div>
      </div>

      <h3>What to include in the request</h3>
      <ul>
        <li>Your registered email address or mobile number</li>
        <li>A clear statement: "I request deletion of all my personal data."</li>
        <li>Verification of identity (we may ask for additional proof)</li>
      </ul>

      <h2>Processing Timeline</h2>
      <p>We will acknowledge your request within <strong>48 hours</strong> and complete deletion within <strong>30 days</strong> of verification, subject to legal retention requirements.</p>

      <h2>What Happens After Deletion</h2>
      <ul>
        <li>Your account becomes permanently inaccessible</li>
        <li>Active bookings / transactions are settled per our refund policy</li>
        <li>Personally identifiable fields are wiped from our active databases</li>
        <li>Encrypted backups are rotated within 90 days</li>
        <li>You will receive email confirmation once deletion is complete</li>
      </ul>

      <h2>Exceptions</h2>
      <p>Some data may be retained where legally required (GST Act, Income Tax Act, PMLA). These are retained in anonymized form where feasible.</p>

      <h2>Contact</h2>
      <p>For any questions about this deletion process, please contact our Data Protection Officer at <a href={`mailto:${COMPANY.email}`} className="text-[#002FA7] hover:underline">{COMPANY.email}</a>.</p>
    </PolicyLayout>
  );
}


export function WhatsAppCompliancePage() {
  return (
    <PolicyLayout title="WhatsApp Business Policy">
      <p className="!text-xs !text-gray-400 !mb-6">Last updated: February 2026</p>

      <h2>Our Commitment</h2>
      <p><strong>{COMPANY.name}</strong> uses WhatsApp Business API solely to serve our registered users and customers. We strictly follow Meta's WhatsApp Business Messaging Policy, Business Policy, and Commerce Policy.</p>

      <h2>Purpose of WhatsApp Messaging</h2>
      <p>We use WhatsApp Business API to send the following transactional and service-related messages to <strong>opt-in users only</strong>:</p>
      <ul>
        <li>Booking confirmations (LPG / utility / recharge)</li>
        <li>Payment and wallet receipts</li>
        <li>Transaction failure or refund notifications</li>
        <li>Support ticket updates</li>
        <li>One-time password (OTP) and authentication codes</li>
        <li>Account alerts (low balance, expiry, suspension)</li>
      </ul>

      <h2>User Opt-In</h2>
      <p>We send WhatsApp messages only to users who have:</p>
      <ul>
        <li>Registered on our platform with an active account, and</li>
        <li>Explicitly consented to receive WhatsApp notifications, and</li>
        <li>Confirmed their mobile number during signup.</li>
      </ul>
      <p>Users can opt-out at any time by replying <strong>STOP</strong> to any WhatsApp message, or via dashboard preference settings.</p>

      <h2>No Spam, No Unsolicited Marketing</h2>
      <p>We do <strong>NOT</strong> use WhatsApp for:</p>
      <ul>
        <li>Unsolicited marketing or promotional blasts</li>
        <li>Messaging phone numbers not registered on our platform</li>
        <li>Third-party advertising or lead generation</li>
        <li>Political campaigning or fundraising</li>
        <li>Any content prohibited under WhatsApp's Commerce Policy (alcohol, tobacco, weapons, adult content, etc.)</li>
      </ul>

      <h2>Data Handling</h2>
      <ul>
        <li>Phone numbers are stored securely and encrypted in transit and at rest.</li>
        <li>Message templates are pre-approved by Meta before use.</li>
        <li>We do not retain message content beyond operational needs (max 90 days).</li>
        <li>Messages pass through a Meta-approved BSP (Business Solution Provider).</li>
      </ul>

      <h2>User Rights</h2>
      <ul>
        <li>Request a copy of WhatsApp messages sent to you → email <a href={`mailto:${COMPANY.email}`} className="text-[#002FA7] hover:underline">{COMPANY.email}</a></li>
        <li>Request immediate removal from the WhatsApp notification list → reply STOP or email us</li>
        <li>Delete your entire WhatsApp interaction history → see <Link to="/data-deletion" className="text-[#002FA7] hover:underline">Data Deletion</Link></li>
      </ul>

      <h2>Report Abuse</h2>
      <p>If you believe you are receiving unsolicited or abusive messages from a number claiming to be us, please report it to <a href={`mailto:${COMPANY.email}`} className="text-[#002FA7] hover:underline">{COMPANY.email}</a> and also directly to Meta via the WhatsApp app.</p>

      <h2>Business Identity</h2>
      <div className="not-prose my-4">
        <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Buildings size={20} className="text-[#002FA7] shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-gray-900">{COMPANY.name}</p>
            <p className="text-gray-600">GSTIN: {COMPANY.gstin}</p>
            <p className="text-gray-600">{COMPANY.address}</p>
            <p className="text-gray-600 mt-2">
              <a href={`mailto:${COMPANY.email}`} className="text-[#002FA7] hover:underline">{COMPANY.email}</a> · {COMPANY.phone}
            </p>
          </div>
        </div>
      </div>

      <h2>Governing Policies</h2>
      <ul>
        <li><a href="https://www.whatsapp.com/legal/business-policy" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">WhatsApp Business Policy</a></li>
        <li><a href="https://www.whatsapp.com/legal/commerce-policy" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">WhatsApp Commerce Policy</a></li>
        <li><a href="https://www.whatsapp.com/legal/business-messaging-policy" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">WhatsApp Business Messaging Policy</a></li>
        <li><Link to="/privacy" className="text-[#002FA7] hover:underline">Our Privacy Policy</Link></li>
        <li><Link to="/terms" className="text-[#002FA7] hover:underline">Our Terms &amp; Conditions</Link></li>
      </ul>
    </PolicyLayout>
  );
}


export function AboutBusinessPage() {
  return (
    <PolicyLayout title="About Our Business">
      <p className="!text-xs !text-gray-400 !mb-6">Last updated: February 2026</p>

      <h2>Company Overview</h2>
      <div className="not-prose my-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-base font-bold text-[#0C1D3F] mb-2">{COMPANY.name}</p>
          <p className="text-sm text-gray-700">A Registered One Person Company (OPC) under the Companies Act, 2013 (India), providing a digital platform for LPG booking, utility bill processing, wallet services, and agency management.</p>
        </div>
      </div>

      <h2>Our Services</h2>
      <ul>
        <li><strong>LPG Booking Platform</strong> — agency-wise bulk booking with staff workflow, OTP/TOTP integration, ledger tracking, complaint resolution.</li>
        <li><strong>Wallet &amp; Deposit Management</strong> — distributor ledgers, deposit approvals, cashback tracking.</li>
        <li><strong>Card Management</strong> — vendor card tracking, balance monitoring, audit trails.</li>
        <li><strong>Support &amp; Ticketing</strong> — end-to-end complaint handling.</li>
      </ul>

      <h2>Legal Registration</h2>
      <div className="not-prose my-4">
        <table className="w-full text-sm border border-gray-200 rounded">
          <tbody>
            <tr className="border-b border-gray-100"><td className="p-2 bg-gray-50 font-medium w-1/3">Entity Type</td><td className="p-2">One Person Company (OPC) Private Limited</td></tr>
            <tr className="border-b border-gray-100"><td className="p-2 bg-gray-50 font-medium">Registered Under</td><td className="p-2">Companies Act, 2013 (Ministry of Corporate Affairs, India)</td></tr>
            <tr className="border-b border-gray-100"><td className="p-2 bg-gray-50 font-medium">GSTIN</td><td className="p-2 font-mono">{COMPANY.gstin}</td></tr>
            <tr className="border-b border-gray-100"><td className="p-2 bg-gray-50 font-medium">State</td><td className="p-2">{COMPANY.state}</td></tr>
            <tr><td className="p-2 bg-gray-50 font-medium">Registered Office</td><td className="p-2">{COMPANY.address}</td></tr>
          </tbody>
        </table>
      </div>

      <h2>Contact Information</h2>
      <div className="not-prose space-y-3 my-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <EnvelopeSimple size={20} className="text-[#002FA7] shrink-0" />
          <div>
            <p className="text-xs text-gray-500">Official Email</p>
            <a href={`mailto:${COMPANY.email}`} className="text-sm font-medium text-[#002FA7] hover:underline">{COMPANY.email}</a>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <Phone size={20} className="text-[#002FA7] shrink-0" />
          <div>
            <p className="text-xs text-gray-500">Business Contact</p>
            <a href={`tel:${COMPANY.phone}`} className="text-sm font-medium text-gray-900">{COMPANY.phone}</a>
          </div>
        </div>
        <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <MapPin size={20} className="text-[#002FA7] shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-gray-500">Office Address</p>
            <p className="text-sm text-gray-700 leading-relaxed">{COMPANY.address}</p>
          </div>
        </div>
      </div>

      <h2>Operating Hours</h2>
      <p>Monday to Saturday · 10:00 AM to 7:00 PM (IST) · Sunday — Support email only</p>

      <h2>Business Commitment</h2>
      <p>We are committed to operating within the legal framework of India, adhering to all applicable regulations including the Companies Act, GST Act, DPDP Act 2023, and IT Act 2000. For any grievances, please refer to our <Link to="/support-help" className="text-[#002FA7] hover:underline">Support &amp; Help</Link> page.</p>
    </PolicyLayout>
  );
}
